"use client"

import { useEffect, useRef, useState } from "react"
import { CATEGORIES, type KexinCategory } from "./data"

const LEAVE_DURATION = 540

type Phase = "ready" | "leaving" | "entering"

// 切换分类时的三段动画: 旧内容右移淡出(leaving) -> 新内容就位隐藏(entering) -> 逐条淡入(ready)
export default function ResearchSection({ onAnalyse }: { onAnalyse: (question: string) => void }) {
  const [selectedKey, setSelectedKey] = useState(CATEGORIES[0].key)
  const [active, setActive] = useState<KexinCategory>(CATEGORIES[0])
  const [phase, setPhase] = useState<Phase>("ready")
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current)
    }
  }, [])

  function handleTabClick(category: KexinCategory) {
    if (category.key === selectedKey) return
    setSelectedKey(category.key)
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current)
    setPhase("leaving")
    leaveTimerRef.current = setTimeout(() => {
      setActive(category)
      setPhase("entering")
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setPhase("ready"))
      })
    }, LEAVE_DURATION)
  }

  const promptClass =
    phase === "leaving"
      ? "opacity-0 translate-y-[18px] scale-[0.985] duration-[220ms]"
      : phase === "entering"
        ? "transition-none opacity-0 translate-y-6 scale-[0.985]"
        : "opacity-100 translate-y-0 scale-100 duration-[680ms]"

  function itemClass() {
    if (phase === "leaving") return "opacity-0 translate-x-[180px]"
    if (phase === "entering") return "transition-none opacity-0 translate-y-3.5"
    return "opacity-100 translate-x-0 translate-y-0"
  }

  function itemStyle(index: number): React.CSSProperties {
    if (phase === "leaving") {
      return { transitionDuration: "320ms", transitionDelay: `${index * 42}ms` }
    }
    if (phase === "entering") {
      return {}
    }
    return { transitionDuration: "680ms", transitionDelay: `${260 + index * 82}ms` }
  }

  const [title1, title2] = active.title.split("\n")

  return (
    <>
      <section className="relative z-10 flex w-full max-w-[1504px] flex-col gap-6 md:gap-10" aria-labelledby="research-title">
        <h2 id="research-title" className="w-full text-left font-serif text-[28px] font-bold leading-9 md:text-center md:text-[40px] md:leading-12">
          可信可以帮你研究
        </h2>
        <div
          className="flex w-full gap-3 overflow-x-auto pb-2 lg:grid lg:grid-cols-5 lg:gap-5 lg:overflow-visible lg:pb-0 [&::-webkit-scrollbar]:hidden"
          role="tablist"
          aria-label="研究分类"
        >
          {CATEGORIES.map((category) => {
            const isActive = category.key === selectedKey
            return (
              <button
                key={category.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTabClick(category)}
                className={`group flex h-[122px] min-w-[190px] shrink-0 cursor-pointer flex-col items-start justify-between p-[22px] text-left transition-all md:h-40 md:p-[30px] lg:min-w-0 ${
                  isActive
                    ? "border border-[#131921] bg-white shadow-[0_2px_3px_rgba(0,0,0,0.1)]"
                    : "border border-transparent bg-[#f5f7fa]"
                }`}
              >
                <span className="text-[26px] font-bold leading-[34px] text-black md:text-[32px] md:leading-10">
                  {category.label}
                </span>
                <span className="inline-flex items-center gap-1 text-base text-[#131921]">
                  查看问题
                  <i
                    className="font_family icon-Arrow_Forward-2 text-2xl leading-none text-[#ff5a00] transition-transform duration-300 group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </span>
              </button>
            )
          })}
        </div>
      </section>

      <section
        id="questions"
        className="relative z-10 grid w-full max-w-[1504px] items-start gap-10 overflow-x-clip lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-5"
        aria-labelledby="question-title"
      >
        <aside className={`w-full transition-all ease-out will-change-transform ${promptClass}`}>
          <h2 id="question-title" className="w-full text-[44px] font-bold leading-[58px] text-balance md:text-[56px] md:leading-[70px]">
            {title1}
            <br />
            {title2}
          </h2>
          <span className="mb-7 mt-6 block h-0.5 w-12 bg-[#131921]" aria-hidden="true" />
          <p className="w-full max-w-[560px] text-base leading-7 text-[#707782]">{active.subtitle}</p>
        </aside>

        <div className="flex w-full flex-col" aria-live="polite">
          {active.questions.map((item, index) => (
            <article
              key={`${active.key}-${index}`}
              className={`group flex w-full flex-col gap-3 border-b border-[#131921] py-6 transition-all ease-out will-change-transform first:pt-0 md:pb-[31px] md:pt-[30px] ${itemClass()}`}
              style={itemStyle(index)}
            >
              <h3 className="font-serif text-xl font-bold leading-[30px] text-[#131921] md:text-[22px] md:leading-8">
                {item.title}
              </h3>
              <p className="text-sm leading-5 text-[#707782]">{item.desc}</p>
              <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:gap-6">
                <div className="flex flex-wrap gap-2">
                  {item.chips.map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex min-h-6 items-center rounded-[5px] bg-[#e4e7ed] px-2 text-xs text-[#606266]"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => onAnalyse(item.title)}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-0.5 whitespace-nowrap text-xs leading-4 text-[#131921] opacity-100 transition-all md:pointer-events-none md:translate-y-1 md:opacity-0 md:group-hover:pointer-events-auto md:group-hover:translate-y-0 md:group-hover:opacity-100"
                >
                  用可信分析
                  <i className="font_family icon-Arrow_Forward-2 text-2xl leading-none" aria-hidden="true" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  )
}
